import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';

/** Busca em pill com debounce; devolve o termo já "assentado" em `onSearch`. */
export const ListSearch: React.FC<{
  placeholder: string;
  onSearch: (term: string) => void;
  className?: string;
}> = ({ placeholder, onSearch, className }) => {
  const [term, setTerm] = useState('');
  const debounced = useDebounce(term, 300);

  useEffect(() => {
    onSearch(debounced.trim());
  }, [debounced, onSearch]);

  return (
    <label
      className={cn(
        'flex h-11 min-h-11 w-full shrink-0 items-center gap-2.5 rounded-full border border-border bg-field px-4 focus-within:border-accent-line',
        className,
      )}
    >
      <Search className="size-4 shrink-0 text-faint" />
      <input
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-faint"
      />
    </label>
  );
};

/** Chip de filtro (ex.: tom). */
export const FilterChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'h-8 shrink-0 rounded-full border px-3 font-semibold text-[12.5px] transition-colors',
      active
        ? 'border-white bg-white text-black'
        : 'border-border bg-raised text-muted-foreground hover:text-white',
    )}
  >
    {children}
  </button>
);

export const ListSummary: React.FC<{ shown: number; total: number; noun: string }> = ({
  shown,
  total,
  noun,
}) => (
  <p className="col-label text-faint">
    Exibindo {shown} de {total} {noun}
  </p>
);

/** Rolagem infinita: ao entrar na tela, pede a próxima página. */
export const InfiniteScrollSentinel: React.FC<{
  hasNextPage: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
}> = ({ hasNextPage, isFetching, onLoadMore }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetching) onLoadMore();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, onLoadMore]);

  if (!hasNextPage) return null;

  return (
    <div ref={ref} className="flex justify-center py-4">
      {isFetching && <Spinner />}
    </div>
  );
};

export const EmptyState: React.FC<{ title: string; hint?: string }> = ({
  title,
  hint,
}) => (
  <div className="flex flex-col items-center gap-1 rounded-[14px] border border-border border-dashed py-16 text-center">
    <p className="font-bold font-display text-white">{title}</p>
    {hint && <p className="text-[13px] text-muted-foreground">{hint}</p>}
  </div>
);
